import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, getCampaigns, createCampaign, launchCampaign, getCampaignProgress } from '../services/api';
import { ArrowLeft, Plus, Rocket, Clock, CheckCircle, Pause, X, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_TEMPLATE = `Bonjour {first_name} 👋

Vous êtes cordialement invité(e) à *{event_name}*
📅 {event_date}
📍 {event_location}

Merci de confirmer votre présence en répondant simplement *OUI* ou *NON*.

À très bientôt ! 🎉`;

function CampaignStatusBadge({ status }) {
  const map = {
    draft:     { cls: 'badge-gray',  label: 'Brouillon', icon: Clock },
    running:   { cls: 'badge-amber', label: 'En cours',  icon: Clock },
    completed: { cls: 'badge-green', label: 'Terminée',  icon: CheckCircle },
    paused:    { cls: 'badge-red',   label: 'Pausée',    icon: Pause },
  };
  const { cls, label } = map[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function CreateModal({ event, onClose, onCreated }) {
  const [form, setForm] = useState({ name: `Invitation - ${event.name}`, message_template: DEFAULT_TEMPLATE });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await createCampaign({ ...form, event_id: event.id });
      toast.success('Campagne créée');
      onCreated();
      onClose();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>Nouvelle campagne</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom de la campagne</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Message WhatsApp</label>
            <textarea value={form.message_template} onChange={e => set('message_template', e.target.value)} rows={10} style={{ fontFamily: 'monospace', fontSize: 13 }} />
          </div>
          <div style={{ background: 'rgba(79,124,255,0.06)', border: '1px solid rgba(79,124,255,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: 'var(--text2)' }}>
            <strong style={{ color: 'var(--accent)' }}>Variables disponibles :</strong>{' '}
            <code>{'{first_name}'}</code> <code>{'{last_name}'}</code> <code>{'{event_name}'}</code> <code>{'{event_date}'}</code> <code>{'{event_location}'}</code>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : 'Créer la campagne'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProgressPoller({ campaignId, onDone }) {
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const { data } = await getCampaignProgress(campaignId);
        setProgress(data);
        if (data.status === 'completed' || data.status === 'paused') {
          clearInterval(poll);
          onDone();
        }
      } catch {}
    }, 2000);
    return () => clearInterval(poll);
  }, [campaignId]);

  if (!progress) return null;
  const pct = progress.total_count > 0 ? Math.round((progress.sent_count / progress.total_count) * 100) : 0;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12.5, color: 'var(--text2)' }}>
        <span>Envoi en cours... {progress.sent_count}/{progress.total_count}</span>
        <span>{pct}%</span>
      </div>
      <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function CampaignsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [launching, setLaunching] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, campRes] = await Promise.all([getEvent(id), getCampaigns(id)]);
      setEvent(evRes.data);
      setCampaigns(campRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleLaunch = async campaign => {
    if (!window.confirm(`Lancer la campagne "${campaign.name}" ?\n\nCela enverra un WhatsApp à tous les invités en statut "En attente".`)) return;
    setLaunching(campaign.id);
    try {
      const { data } = await launchCampaign(campaign.id);
      toast.success(`Campagne lancée — ${data.total} messages en cours d'envoi`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lancement');
      setLaunching(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/events/${id}`)}>
          <ArrowLeft size={14} /> {event?.name || 'Retour'}
        </button>
      </div>

      <div className="page-header">
        <div>
          <h1>Campagnes WhatsApp</h1>
          <p className="text-muted">Gérez les envois d'invitations pour {event?.name}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={15} /> Nouvelle campagne
        </button>
      </div>

      {/* Info box */}
      <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 24, display: 'flex', gap: 10, fontSize: 13, color: 'var(--text2)' }}>
        <Info size={16} color="var(--amber)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>Le lancement envoie les messages à tous les invités avec le statut <strong style={{ color: 'var(--text)' }}>En attente</strong>. Les invités ayant déjà répondu ne reçoivent pas de nouveau message. Un délai de 1,5s est appliqué entre chaque envoi.</div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : campaigns.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Rocket />
            <h3>Aucune campagne</h3>
            <p>Créez une campagne pour envoyer vos invitations WhatsApp.</p>
            <button className="btn btn-primary mt-4" onClick={() => setShowCreate(true)}>
              <Plus size={15} /> Créer une campagne
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {campaigns.map(c => (
            <div key={c.id} className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <h3>{c.name}</h3>
                    <CampaignStatusBadge status={c.status} />
                  </div>
                  <p className="text-sm text-muted" style={{ marginBottom: 10 }}>
                    Créée le {new Date(c.created_at).toLocaleDateString('fr-FR')}
                    {c.started_at && ` · Lancée le ${new Date(c.started_at).toLocaleDateString('fr-FR')}`}
                    {c.completed_at && ` · Terminée le ${new Date(c.completed_at).toLocaleDateString('fr-FR')}`}
                  </p>
                  {c.total_count > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12.5, color: 'var(--text2)' }}>
                        <span>{c.sent_count} / {c.total_count} messages envoyés</span>
                        <span>{Math.round((c.sent_count / c.total_count) * 100)}%</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${Math.round((c.sent_count / c.total_count) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                  {c.status === 'running' && launching !== c.id && (
                    <ProgressPoller campaignId={c.id} onDone={load} />
                  )}
                </div>
                <div style={{ flexShrink: 0 }}>
                  {c.status === 'draft' && (
                    <button className="btn btn-success" onClick={() => handleLaunch(c)} disabled={!!launching}>
                      {launching === c.id ? <span className="spinner" /> : <Rocket size={15} />}
                      Lancer
                    </button>
                  )}
                  {c.status === 'completed' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleLaunch(c)} title="Renvoyer aux non-répondants">
                      <Rocket size={14} /> Relancer
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && event && (
        <CreateModal event={event} onClose={() => setShowCreate(false)} onCreated={load} />
      )}
    </div>
  );
}
